// Postcode validation and distance calculation utilities

// Use var instead of const to allow redeclaration (though file should only load once)
// Postcode validation now uses api.postcodes.io (no API key required)

// ====================================
// DEBUG CONFIGURATION
// ====================================
// Set DEBUG_MODE to false to disable all debug logging in production
// This affects both postcode-utils.js and postcode-checker.js
// Usage:
//   debugLog(...)   - Normal debug messages (controlled by DEBUG_MODE)
//   debugWarn(...)  - Warning messages (controlled by DEBUG_MODE)
//   debugError(...) - Error messages (always shown, even when DEBUG_MODE is false)
// ====================================
var DEBUG_MODE =
    typeof window !== "undefined" && typeof window.DEBUG_MODE !== "undefined"
        ? window.DEBUG_MODE
        : false; // Set to false to disable all debug logging
if (typeof window !== "undefined") {
    window.DEBUG_MODE = DEBUG_MODE;
}

/**
 * Debug logger - only logs when DEBUG_MODE is true
 * @param {...any} args - Arguments to log
 */
function debugLog(...args) {
    const enabled =
        typeof window !== "undefined" && typeof window.DEBUG_MODE !== "undefined"
            ? window.DEBUG_MODE
            : DEBUG_MODE;
    if (enabled) {
        console.log(...args);
    }
}

/**
 * Debug warning - only logs when DEBUG_MODE is true
 * @param {...any} args - Arguments to log as warning
 */
function debugWarn(...args) {
    const enabled =
        typeof window !== "undefined" && typeof window.DEBUG_MODE !== "undefined"
            ? window.DEBUG_MODE
            : DEBUG_MODE;
    if (enabled) {
        console.warn(...args);
    }
}

/**
 * Debug error - always logs errors regardless of DEBUG_MODE
 * @param {...any} args - Arguments to log as error
 */
function debugError(...args) {
    console.error(...args);
}

// ====================================
// CONFIGURATION CONSTANTS
// ====================================
var DEFAULT_POSTCODE_CONFIG = {
    MOBILE_BREAKPOINT: 768,
    MAX_COLLECTION_DISTANCE_MILES: 40,
    FREE_COLLECTION_THRESHOLD: 29.0,
    COLLECTION_PRICE: 2.5,
    DEFAULT_CUTOFF_TIME: "16:00",
    CUTOFF_TIME_OVERRIDE: "", // Optional test override, leave empty when not in use
    ALTERNATIVE_COLLECTION_DAYS: 2,
    DEFAULT_COLLECTION_DAYS: 1,
};

var POSTCODE_CONFIG =
    typeof window !== "undefined" && window.POSTCODE_CONFIG
        ? { ...DEFAULT_POSTCODE_CONFIG, ...window.POSTCODE_CONFIG }
        : { ...DEFAULT_POSTCODE_CONFIG };
if (typeof window !== "undefined") {
    window.POSTCODE_CONFIG = POSTCODE_CONFIG;
}

// ====================================
// DATE & TIME UTILITIES
// ====================================

/**
 * Check if current time is after a given cutoff time
 * @param {string} cutoffTime - Time in HH:MM format (e.g., "16:00")
 * @returns {boolean} - True if current time is after cutoff
 */
function isAfterCutoffTime(cutoffTime = POSTCODE_CONFIG.DEFAULT_CUTOFF_TIME) {
    const configuredOverride =
        POSTCODE_CONFIG && typeof POSTCODE_CONFIG.CUTOFF_TIME_OVERRIDE === "string"
            ? POSTCODE_CONFIG.CUTOFF_TIME_OVERRIDE.trim()
            : "";

    const effectiveCutoffTime = configuredOverride || cutoffTime;
    debugLog("[effectiveCutoffTime] ", effectiveCutoffTime);
    const [cutoffHour, cutoffMinute] = effectiveCutoffTime.split(":").map(Number);
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const cutoffMinutes = cutoffHour * 60 + cutoffMinute;
    return currentMinutes >= cutoffMinutes;
}

/**
 * Format a date as "11 Jan (Sun)"
 * @param {Date} date - The date to format
 * @returns {string} - Formatted date string
 */
function formatDeliveryDate(date) {
    const day = date.getDate();
    const month = date.toLocaleDateString("en-GB", { month: "short" });
    const weekday = date.toLocaleDateString("en-GB", { weekday: "short" });
    return `${day} ${month} (${weekday})`;
}

/**
 * Calculate earliest delivery date based on days offset and cutoff time
 * @param {number} daysOffset - Base number of days to add
 * @param {boolean} isAfterCutoff - Whether current time is after cutoff
 * @returns {Date} - The calculated delivery date
 */
function calculateEarliestDate(daysOffset, isAfterCutoff) {
    const extraDay = isAfterCutoff ? 1 : 0;
    const date = new Date();
    date.setDate(date.getDate() + daysOffset + extraDay);
    return date;
}

/**
 * Format price as "FREE" or "£X.XX"
 * @param {number} price - The price to format
 * @returns {string} - Formatted price string
 */
function formatPrice(price) {
    return price === 0 ? "FREE" : "£" + price.toFixed(2);
}

// ====================================
// PRICING UTILITIES
// ====================================

/**
 * Calculate collection price based on product price
 * @param {number} productPrice - The product price
 * @returns {number} - The collection price (0 for free, or COLLECTION_PRICE)
 */
function calculateCollectionPrice(productPrice) {
    return productPrice >= POSTCODE_CONFIG.FREE_COLLECTION_THRESHOLD
        ? 0
        : POSTCODE_CONFIG.COLLECTION_PRICE;
}

/**
 * Calculate collection days based on store data
 * @param {object} store - The store object
 * @returns {number} - Number of days for collection
 */
function calculateCollectionDays(store) {
    debugLog("COLLECTION DAYS")

    if (!store) {
        return POSTCODE_CONFIG.DEFAULT_COLLECTION_DAYS;
    }

    const hasNextDayOverride =
        store.next_day_override === true || store.next_day_override === "true";

    if (hasNextDayOverride) {
        debugLog("HAS NEXT DAY OVERRIDE")
        return POSTCODE_CONFIG.ALTERNATIVE_COLLECTION_DAYS;
    }

    return POSTCODE_CONFIG.DEFAULT_COLLECTION_DAYS;
}

// ====================================
// POSTCODE VALIDATION
// ====================================

function normalizePostcode(postcode) {
    return (postcode || "").trim().toUpperCase();
}

// More complete UK postcode regex that accepts all valid formats, with or without a space
var postcodeRegex = /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;

function isValidPostcodeFormat(postcode) {
    const safeRegex =
        postcodeRegex instanceof RegExp
            ? postcodeRegex
            : /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i;

    const value = (postcode || "").trim();
    const isValid = safeRegex.test(value);
    debugLog("POSTCODE VALIDATION", isValid, value);
    return isValid;
}

/**
 * Format postcode with a single space before the final three characters for API calls.
 * E.g. "b11db" -> "B11 DB"
 */
function formatPostcodeForAPI(postcode) {
    debugLog("[POSTCODE CLEANUP]", postcode);
    const cleaned = (postcode || "").toUpperCase().replace(/\s+/g, "");
        debugLog("[POSTCODE CLEANUP cleaned]", cleaned);
    if (cleaned.length <= 3) return cleaned;
    debugLog("[POSTCODE CLEANUP else]", cleaned.slice(0, cleaned.length - 3) + " " + cleaned.slice(cleaned.length - 3));
    return cleaned.slice(0, cleaned.length - 3) + " " + cleaned.slice(cleaned.length - 3);
}

function getOutboundCode(postcode) {
    const normalized = (postcode || "").replace(/\s+/g, "").toUpperCase();
    return normalized.slice(0, -3) + "_";
}

function isInPostalZone(outbound, postalZone) {
    if (!postalZone || !postalZone.postcodes) return false;
    const outboundPrefix = outbound.replace(/_$/, "");
    return postalZone.postcodes.some((code) => {
        const prefix = (code || "").replace(/_$/, "");
        return outboundPrefix.startsWith(prefix);
    });
}

/**
 * Check if outbound postcode (e.g. AB35_) is inside blackout prefixes.
 * Uses prefix matching on the outbound stem (without trailing underscore).
 * @param {string} outbound - Outbound with underscore suffix
 * @param {{postcodes?: string[]}} blackoutZone - Blackout zone object with postcode prefixes
 * @returns {boolean}
 */
function isInBlackoutZone(outbound, blackoutZone) {
    debugLog("BLACKOUT ", blackoutZone);
    if (!outbound || !blackoutZone || !Array.isArray(blackoutZone.postcodes)) {
        return false;
    }

    const outboundWithUnderscore = `${String(outbound)
        .replace(/\s+/g, "")
        .toUpperCase()
        .replace(/_+$/, "")}_`;
    const outboundStem = outboundWithUnderscore.replace(/_$/, "");

    for (const code of blackoutZone.postcodes) {
        const blackoutWithUnderscore = `${String(code || "")
            .replace(/\s+/g, "")
            .toUpperCase()
            .replace(/_+$/, "")}_`;
        const blackoutStem = blackoutWithUnderscore.replace(/_$/, "");

        if (outboundStem.startsWith(blackoutStem)) {
            return true;
        }
    }

    return false;
}

// Global failed postcode tracking to prevent API spam
var lastFailedPostcode =
    typeof window !== "undefined" && typeof window.lastFailedPostcode !== "undefined"
        ? window.lastFailedPostcode
        : null;
if (typeof window !== "undefined") {
    window.lastFailedPostcode = lastFailedPostcode;
}

/**
 * Check if a postcode is the same as the last failed one
 * @param {string} postcode - Postcode to check
 * @returns {boolean} - True if this is the last failed postcode
 */
function isLastFailedPostcode(postcode) {
    if (!lastFailedPostcode) return false;
    return normalizePostcode(postcode) === lastFailedPostcode;
}

/**
 * Clear the failed postcode tracking
 */
function clearFailedPostcode() {
    debugLog(
        "[FAILED PC TRACKING] Clearing failed postcode:",
        lastFailedPostcode,
    );
    lastFailedPostcode = null;
}

async function validatePostcodeWithAPI(postcode, showErrorCb) {
    // Normalize first (trim + uppercase), then validate format.
    const normalizedPostcode = normalizePostcode(postcode);
    // compact form without any spaces for length checks
    const compactPostcode = (normalizedPostcode || "").replace(/\s+/g, "");
    if (!compactPostcode || compactPostcode.length < 5) {
        return { valid: false, reason: "empty" };
    }

    if (!isValidPostcodeFormat(normalizedPostcode)) {
        if (showErrorCb)
            showErrorCb("Please enter a valid UK postcode format.");
        return { valid: false, reason: "format" };
    }

    // Check if this is the same postcode that previously failed
    if (lastFailedPostcode && normalizedPostcode === lastFailedPostcode) {
        debugLog(
            "[FAILED PC TRACKING] Blocking resubmission of failed postcode:",
            lastFailedPostcode,
        );
        if (showErrorCb)
            showErrorCb(
                "This postcode is invalid. Please try a different one.",
            );
        return {
            valid: false,
            reason: "previously_failed",
            blockedByTracking: true,
        };
    }

    if (localStorage.getItem("pcc-valid-pc")) {
        var currentPostcode = JSON.parse(localStorage.getItem("pcc-valid-pc")).postcode;
        debugLog("HAS STORAGE ", currentPostcode);
        debugLog("compare ", currentPostcode);

        //if inputted postcode equals stored postcode skip API call 
        if (normalizedPostcode == currentPostcode) {
            let result = localStorage.getItem("pcc-valid-pc");
            debugLog("SKIPPING API CALL ");
            return { valid: true, data: result }
        }

    }

    try {
        debugLog("[PC API VALIDATION CALLED]");

        const apiPostcode = formatPostcodeForAPI(normalizedPostcode);
        debugLog("[PC API] Using formatted postcode for API:", apiPostcode);
        const response = await fetch(
            `https://api.postcodes.io/postcodes/${encodeURIComponent(apiPostcode)}`,
        );

        if (!response.ok) {
            debugWarn("Postcode validation failed:", response.status);
            // Store failed postcode to prevent retry
            lastFailedPostcode = normalizedPostcode;
            debugLog(
                "[FAILED PC TRACKING] Stored failed postcode (API error):",
                lastFailedPostcode,
            );
            if (showErrorCb)
                showErrorCb("Unable to validate postcode. Please try again.");
            return { valid: false, reason: "api_error" };
        }

        const data = await response.json();
        const result = data && data.result ? data.result : null;

        debugLog("[VALID] ", data);
        debugLog("[RESULT] ", result);

        if (!result || !result.latitude || !result.longitude) {
            // Store failed postcode to prevent retry
            lastFailedPostcode = normalizedPostcode;
            debugLog(
                "[FAILED PC TRACKING] Stored failed postcode (no result):",
                lastFailedPostcode,
            );
            if (showErrorCb)
                showErrorCb("Postcode not found. Please try again.");
            return { valid: false, reason: "no_result" };
        }

        // Success - clear any previous failed postcode
        if (lastFailedPostcode) {
            debugLog(
                "[FAILED PC TRACKING] Validation successful, clearing failed tracking",
            );
        }
        lastFailedPostcode = null;

        let postcodeData = {
            postcode: normalizedPostcode,
            latitude: result.latitude,
            longitude: result.longitude,
        };
        localStorage.setItem(
            "pcc-valid-pc",
            JSON.stringify(postcodeData),
        );

        return { valid: true, data: result };
    } catch (err) {
        debugError("Postcode API error:", err);
        // Store failed postcode to prevent retry
        lastFailedPostcode = normalizedPostcode;
        debugLog(
            "[FAILED PC TRACKING] Stored failed postcode (exception):",
            lastFailedPostcode,
        );
        if (showErrorCb)
            showErrorCb("Unable to validate postcode. Please try again.");
        return { valid: false, reason: "exception" };
    }
}

//HAVERSINE EQUATION

function deg2rad(deg) {
    return deg * (Math.PI / 180);
}

function getDistanceFromLatLonInMi(lat1, lon1, lat2, lon2) {
    var R = 6371; // Radius of the earth in km
    var dLat = deg2rad(lat2 - lat1);
    var dLon = deg2rad(lon2 - lon1);
    var a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) *
            Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c; // Distance in km
    var miles = d / 1.609344; //Convert km to miles
    return miles;
}

function calculateClosestStores(postcodeData) {
    const storesScript = document.querySelector("[data-store-locations]");
    if (!storesScript) return [];

    const storesData = JSON.parse(storesScript.textContent);
    const stores = storesData.stores || [];
    const closestStoreCount = 3;

    debugLog("STORES ", stores);
    debugLog("postCode Data ", postcodeData);

    // Calculate distance for each store
    const storesWithDistance = stores.map((store) => ({
        ...store,
        distance: getDistanceFromLatLonInMi(
            postcodeData.latitude,
            postcodeData.longitude,
            store.latitude,
            store.longitude,
        ),
    }));

    // Sort by distance and get top 3
    const sorted = storesWithDistance.sort((a, b) => a.distance - b.distance);
    return sorted.slice(0, closestStoreCount);
}

function getProductCollectionType() {
    const collectionsScript = document.querySelector(
        "[data-product-collections]",
    );
    if (!collectionsScript) {
        debugWarn("Product collections data not found");
        return {
            isCollectable: false,
            allowedTypes: [],
        };
    }

    const collectionsData = JSON.parse(collectionsScript.textContent);
    const collections = collectionsData.collections || [];

    debugLog("PRODUCT COLLECTIONS ", collections);

    // Define collection type mappings
    const collectionTypes = {
        lockers: "collectable-products-lockers",
        selectedStores: "collectable-products-selected-stores",
        storesWithoutFridge: "collectable-products-stores-without-a-fridge",
        storesWithFridge: "collectable-products-stores-with-a-fridge",
    };

    // Check which collection types the product belongs to
    const allowedTypes = [];

    if (collections.some((c) => c.handle === collectionTypes.lockers)) {
        allowedTypes.push("locker");
    }
    if (collections.some((c) => c.handle === collectionTypes.selectedStores)) {
        allowedTypes.push("selected-store");
    }
    if (
        collections.some(
            (c) => c.handle === collectionTypes.storesWithoutFridge,
        )
    ) {
        allowedTypes.push("store-without-fridge");
    }
    if (
        collections.some((c) => c.handle === collectionTypes.storesWithFridge)
    ) {
        allowedTypes.push("store-with-fridge");
    }

    return {
        isCollectable: allowedTypes.length > 0,
        allowedTypes: allowedTypes,
        rawCollections: collections,
    };
}

function isClosestStoreWithinRange(stores, maxDistanceMiles = 40) {
    if (!stores || stores.length === 0) {
        return false;
    }

    const closestStore = stores[0];
    return closestStore.distance <= maxDistanceMiles;
}

function filterStoresByCollectionType(stores, collectionType) {
    const typeMap = {
        "collectable-products-selected-stores": {
            store_type: "Store",
            store_size: "Large",
        },
        "collectable-products-stores-without-a-fridge": {
            store_type: "Store",
            store_size: "Small",
        },
        "collectable-products-stores-with-a-fridge": {
            store_type: "Store",
            store_size: "Small",
        },
        "collectable-products-lockers": {
            store_type: "Locker",
            store_size: null,
        },
    };

    const filter = typeMap[collectionType];
    // debugLog(
    //     "[FILTER STORES] Collection:",
    //     collectionType,
    //     "| Filter:",
    //     filter,
    // );

    // If collection type not in map (non-collectable collection), return empty array
    if (!filter) {
        // debugLog(
        //     "[FILTER STORES] No filter found for collection type, returning empty array",
        // );
        return [];
    }

    // debugLog("[FILTER STORES] Checking", stores.length, "stores");
    // debugLog(
    //     "[FILTER STORES] Looking for store_type:",
    //     filter.store_type,
    //     "| store_size:",
    //     filter.store_size,
    // );

    const filtered = stores.filter((store) => {
        const matchesType = store.store_type === filter.store_type;
        const matchesSize =
            filter.store_size === null ||
            store.store_size === filter.store_size;
        const matches = matchesType && matchesSize;

        if (matches) {
            // debugLog(
            //     "[FILTER STORES] ✓ Match:",
            //     store.title,
            //     "|",
            //     store.store_type,
            //     "|",
            //     store.store_size,
            // );
        }

        return matches;
    });

    // debugLog("[FILTER STORES] Result:", filtered.length, "stores matched");
    return filtered;
}

function calculateCollectionAvailability(postcodeData, productPrice) {
    const collectionInfo = getProductCollectionType();

    if (!collectionInfo.isCollectable) {
        return {
            available: false,
            reason: "Product not available for collection",
        };
    }

    // Get all stores with distances
    const storesScript = document.querySelector("[data-store-locations]");
    if (!storesScript) {
        return {
            available: false,
            reason: "Store location data not found",
        };
    }

    const storesData = JSON.parse(storesScript.textContent);
    let stores = storesData.stores || [];

    // Calculate distances for all stores
    stores = stores.map((store) => ({
        ...store,
        distance: getDistanceFromLatLonInMi(
            postcodeData.latitude,
            postcodeData.longitude,
            store.latitude,
            store.longitude,
        ),
    }));

    // Filter stores by collection type - only check collectable collections
    const collectableHandles = [
        "collectable-products-selected-stores",
        "collectable-products-stores-without-a-fridge",
        "collectable-products-stores-with-a-fridge",
        "collectable-products-lockers",
    ];

    const collections = collectionInfo.rawCollections;
    let eligibleStores = [];

    debugLog("[COLLECTION FILTER] Starting store filtering");
    debugLog(
        "[COLLECTION FILTER] All product collections:",
        collections.map((c) => c.handle || c),
    );
    debugLog(
        "[COLLECTION FILTER] Collectable collection types found:",
        collectionInfo.allowedTypes,
    );

    for (const collection of collections) {
        const collectionHandle =
            typeof collection === "string" ? collection : collection.handle;

        // Only process if it's a collectable collection
        if (collectableHandles.includes(collectionHandle)) {
            debugLog(
                "[COLLECTION FILTER] Processing collectable collection:",
                collectionHandle,
            );
            const filtered = filterStoresByCollectionType(
                stores,
                collectionHandle,
            );
            debugLog(
                "[COLLECTION FILTER] Found",
                filtered.length,
                "matching stores for",
                collectionHandle,
            );
            filtered.forEach((store) => {
                debugLog(
                    "  -",
                    store.title,
                    "|",
                    store.store_type,
                    "|",
                    store.store_size,
                );
            });
            eligibleStores = [...eligibleStores, ...filtered];
        }
    }

    debugLog(
        "[COLLECTION FILTER] Total eligible stores before deduplication:",
        eligibleStores.length,
    );

    // Remove duplicates and sort by distance
    eligibleStores = [
        ...new Map(
            eligibleStores.map((store) => [store.title, store]),
        ).values(),
    ];
    eligibleStores.sort((a, b) => a.distance - b.distance);

    // Check if closest store is within 40 miles
    if (!isClosestStoreWithinRange(eligibleStores, 40)) {
        return {
            available: false,
            reason: "No collection points within 40 miles",
        };
    }

    const closestStore = eligibleStores[0];

    // Calculate pricing and earliest date using helper functions
    const earliestDays = calculateCollectionDays(closestStore);
    const collectionPrice = calculateCollectionPrice(productPrice);

    return {
        available: true,
        closestStore: closestStore,
        eligibleStores: eligibleStores.slice(0, 3), // Top 3 closest
        earliestDays: earliestDays,
        collectionPrice: collectionPrice,
        collectionInfo: collectionInfo,
    };
}

// Helper: Get delivery zones from DOM
function getDeliveryZones() {
    const zonesScript = document.querySelector("[data-pc-zones]");
    if (!zonesScript) return null;

    const zoneData = JSON.parse(zonesScript.textContent);
    return zoneData.deliveryZones || [];
}

// Helper: Get product collections from DOM
function getProductCollections() {
    const collectionsScript = document.querySelector(
        "[data-product-collections]",
    );
    if (!collectionsScript) return null;

    const collectionsData = JSON.parse(collectionsScript.textContent);
    return collectionsData.collections || [];
}

// Helper: Find delivery collection with min_fulfillment_price
function getDeliveryCollectionPrice(collections) {
    const deliveryCollection = collections.find(
        (c) =>
            (typeof c === "string" && c === "available-for-delivery") ||
            (typeof c === "object" && c.handle === "available-for-delivery"),
    );

    if (!deliveryCollection) return null;
    return deliveryCollection.min_fulfillment_price || 0;
}

// Helper: Get postal zone from DOM
function getPostalZone() {
    const zonesScript = document.querySelector("[data-pc-zones]");
    if (!zonesScript) return null;

    const zoneData = JSON.parse(zonesScript.textContent);
    return zoneData.postalZone || null;
}

// Helper: Find matching delivery zone for postcode (includes postal zone)
function findDeliveryZone(postcodeValue, deliveryZones) {
    const outbound = getOutboundCode(postcodeValue);

    // First check delivery zones (zone-1, zone-2, zone-3)
    const deliveryZone = deliveryZones.find((zone) =>
        zone.postcodes.includes(outbound),
    );
    if (deliveryZone) return deliveryZone;

    // If not in delivery zones, check postal zone
    const postalZone = getPostalZone();
    if (postalZone && isInPostalZone(outbound, postalZone)) {
        // Return postal zone formatted like a delivery zone
        return {
            title: postalZone.title || "Postal Zone",
            handle: "postal-zone",
            postcodes: postalZone.postcodes,
        };
    }

    return null;
}

// Helper: Calculate zone-specific pricing and delivery days
function calculateZonePricing(zoneHandle, basePrice, isAfterCutoff) {
    const zoneConfig = {
        "zone-1": { daysIfBefore: 1, daysIfAfter: 2, priceOffset: 0 },
        "zone-2": { daysIfBefore: 1, daysIfAfter: 2, priceOffset: 4 },
        "zone-3": { daysIfBefore: 2, daysIfAfter: 3, priceOffset: 4 },
        "postal-zone": { daysIfBefore: 2, daysIfAfter: 3, priceOffset: 4 }, // Same as zone-3
    };

    const config = zoneConfig[zoneHandle] || {
        daysIfBefore: 2,
        daysIfAfter: 3,
        priceOffset: 4,
    };

    return {
        earliestDays: isAfterCutoff ? config.daysIfAfter : config.daysIfBefore,
        deliveryPrice: basePrice + config.priceOffset,
    };
}

//lolas delivery
function calculateAvailabilityForDelivery(
    postcodeData,
    postcodeValue,
    productPrice,
) {
    debugLog("[DELIVERY CALC] Starting calculation");
    debugLog("  - Postcode:", postcodeValue);
    debugLog("  - Product Price:", productPrice);

    // Get delivery zones
    const deliveryZones = getDeliveryZones();
    if (!deliveryZones) {
        debugLog("[DELIVERY CALC] ERROR: Delivery zones data not found");
        return { available: false, reason: "Delivery zones data not found" };
    }
    debugLog("[DELIVERY CALC] Found", deliveryZones.length, "delivery zones");

    // Get product collections
    const collections = getProductCollections();
    if (!collections) {
        debugLog("[DELIVERY CALC] ERROR: Product collections data not found");
        return {
            available: false,
            reason: "Product collections data not found",
        };
    }
    debugLog("[DELIVERY CALC] Collections:", collections);

    // Check if product is available for delivery and get base price
    const baseDeliveryPrice = getDeliveryCollectionPrice(collections);
    if (baseDeliveryPrice === null) {
        debugLog(
            "[DELIVERY CALC] ERROR: Product not in available-for-delivery collection",
        );
        return {
            available: false,
            reason: "Product not available for delivery",
        };
    }
    debugLog("[DELIVERY CALC] Product is available for delivery");
    debugLog("[DELIVERY CALC] Base delivery price:", baseDeliveryPrice);

    // Find matching delivery zone (includes postal zone)
    const matchedZone = findDeliveryZone(postcodeValue, deliveryZones);
    if (!matchedZone) {
        debugLog(
            "[DELIVERY CALC] ERROR: Postcode not in any delivery or postal zone",
        );
        return {
            available: false,
            reason: "Postcode not in any delivery or postal zone",
        };
    }
    debugLog(
        "[DELIVERY CALC] Matched zone:",
        matchedZone.title,
        "(",
        matchedZone.handle,
        ")",
    );

    // Check cutoff time
    const container = document.querySelector("[data-cutoff-time]");
    const cutoffTime =
        container && container.dataset.cutoffTime
            ? container.dataset.cutoffTime
            : POSTCODE_CONFIG.DEFAULT_CUTOFF_TIME;
    const configuredOverride =
        POSTCODE_CONFIG && typeof POSTCODE_CONFIG.CUTOFF_TIME_OVERRIDE === "string"
            ? POSTCODE_CONFIG.CUTOFF_TIME_OVERRIDE.trim()
            : "";
    const effectiveCutoffTime = configuredOverride || cutoffTime;
    const isAfterCutoff = isAfterCutoffTime(cutoffTime);
    debugLog(
        "[DELIVERY CALC] Cutoff time (raw):",
        cutoffTime,
        "| effective:",
        effectiveCutoffTime,
        "| After cutoff:",
        isAfterCutoff,
    );

    // Calculate pricing and delivery days for zone
    const { earliestDays: baseEarliestDays, deliveryPrice } = calculateZonePricing(
        matchedZone.handle,
        baseDeliveryPrice,
        isAfterCutoff,
    );

    // Nationwide (postal-zone) delivery uses base zone timing only.
    let earliestDays = baseEarliestDays;
    if (matchedZone.handle === "postal-zone") {
        debugLog(
            "[DELIVERY CALC] postal-zone uses base timing; next_day_override adjustment disabled",
        );
    }

    debugLog("[DELIVERY CALC]", matchedZone.handle, "logic applied");
    debugLog(
        "[DELIVERY CALC] Final values - Days:",
        earliestDays,
        "| Price:",
        deliveryPrice,
    );

    return {
        available: true,
        zone: matchedZone,
        earliestDays,
        deliveryPrice,
        minFulfillmentPrice: baseDeliveryPrice,
    };
}
